import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { Subject } from 'rxjs';
import { FilterNGraphModel } from 'src/app/models/filterNgraph/FilterNGraphModel';
import { BaseFromFilterEvent } from 'src/app/models/filterNgraph/BaseFromFilterEvent';
import { DataGroupedByAxis } from 'src/swagger-gen/web-secured/models';
import { FilterNGraphConfig } from 'src/app/models/filterNgraph/FilterNGraphConfig';

@Injectable({
  providedIn: 'root'
})
export class FilterNGraphEchartService {

  graphs: FilterNGraphModel[] = [];

  constructor() { }

  private getGraph(graphID: string){
    return this.graphs.find(gc => gc.config.id == graphID); //récupère tous les infors d'un graph
  }

  getTruncateState(graphID: string): boolean{
    const graph = this.getGraph(graphID);
    return graph.config.hasTruncatedDate;
  }

  getStartDate(graphID: string): string{
    const graph = this.getGraph(graphID);
    return formatDate(
      graph.config.filterConfig.paramStartDate,
      'dd/MM/yyyy HH:mm:ss',
      'fr-FR'
    );
  }

  getEndDate(graphID: string): string{
    const graph = this.getGraph(graphID);
    return formatDate(
      graph.config.filterConfig.paramEndDate,
      'dd/MM/yyyy HH:mm:ss',
      'fr-FR'
    );
  }


  /***************************************************/
  /*****************SERVICE PROVIDED******************/
  /***************************************************/

  /**
   * Allow the component using the service to add a graph config to the pool
   * @param title 
   * @param selectedTagsUid 
   */
  initGraphs(title: string, selectedTagsUid: Array<string>){
    const graphModel = new FilterNGraphModel(title, selectedTagsUid);
    this.graphs.push(graphModel);
    graphModel.updateConfig.next(graphModel.config);
  }

  /**
   * Allow the component using the service to access the targeted graph configuration updates subject to allow subscription
   * @param graphID 
   * @returns 
   */
  getConfigSubject(graphID: string): Subject<FilterNGraphConfig> {
    const graph = this.getGraph(graphID);
    return graph.updateConfig;
  }
  
  /**
   * Allow the component using the service to update the targeted graph loading state
   * @param graphID 
   * @param state 
   */
  changeGraphLoadingState(graphID: string, state: boolean){
    const graph = this.getGraph(graphID);
    if(graph) graph.config.isRequesting = state;
  }

  /**
   * Allow the component using the service to trigger a data update for the targeted graph
   * @param data 
   * @param config 
   */
  buildChart(data: Array<DataGroupedByAxis>, config: FilterNGraphConfig){
    this.getDataSubject(config.id).next({
      paramStartDate: config.filterConfig.paramStartDate,
      paramEndDate: config.filterConfig.paramEndDate,
      
      title: config.title,
      data: data,
    })
  }

  /**
   * Allow the component using the service to reset the graph pool when destroyed
   */
  resetGraphs(){
    this.graphs = [];
  }

  /**
   * Allow the component using the service to resize the graph range when results are truncated
   * @param date 
   * @param graphID 
   */
  truncateStartDate(date: string, graphID: string){
    const graph = this.getGraph(graphID);
    if (graph)  {
      graph.config.filterConfig.paramStartDate = date;
      graph.config.hasTruncatedDate = true;
    }
  }

  /**
   * Allow the component using the service to mask the truncate message
   * @param graphID 
   */
  resetTruncateState(graphID: string){
    const graph = this.getGraph(graphID);
    if (graph)  {
      graph.config.hasTruncatedDate = false;
    }
  }


  /***************************************************/
  /*******************FILTER USAGE********************/
  /***************************************************/

  /**
   * React to users changes of the filters inputs
   * @param params 
   * @param graphID 
   */
  updateFilterConfig(params: BaseFromFilterEvent, graphID: string){
    const graph = this.getGraph(graphID);
    if (graph)  {
      graph.config.filterConfig = params;
      graph.updateConfig.next(graph.config);
    }
  }

  /**
   * React to threshold mode toggle
   * @param threshold 
   * @param graphID 
   */
  updateThreshold(threshold: boolean, graphID: string){
    const graph = this.getGraph(graphID);
    if(graph) graph.thresholdSubject.next(threshold);
  }

  /***************************************************/
  /********************GRAPH USAGE********************/
  /***************************************************/

  /**
   * React to graph units updates
   * @param units 
   * @param graphID 
   */
  updateUnits(units: Array<string>, graphID: string){
    const graph = this.getGraph(graphID);
    if(graph) graph.unitSubject.next(units);
  }

  /**
   * Provide the targeted graph threshold toggle subject to allow subscription
   * @param graphID 
   * @returns 
   */
  getThresholdSubject(graphID: string): Subject<boolean> {
    const graph = this.getGraph(graphID);
    return graph.thresholdSubject;
  }

  /**
   * Provide the targeted graph data updates subject to allow subscription
   * @param graphID 
   * @returns 
   */
  getDataSubject(graphID: string): Subject<any> {
    const graph = this.getGraph(graphID);
    return graph.dataSubject;
  }

  /**
   * Check the targeted graph loading state (use by template rendering)
   * @param graphID 
   * @returns 
   */
  getGraphLoadingState(graphID: string): boolean {
    const graph = this.getGraph(graphID);
    return graph.config.isRequesting;
  }

  /***************************************************/
  /********************UNUSED SO FAR******************/
  /***************************************************/

  /**
   * Provide the targeted graph unit updates subject to allow subscription
   * @param graphID 
   * @returns 
   */
  getUnitSubject(graphID: string): Subject<Array<string>> {
    const graph = this.getGraph(graphID);
    return graph.unitSubject;
  }

}
